import { Button as KendoButton } from '@progress/kendo-react-buttons';

/**
 * A single button component that wraps Kendo's Button component.
 * This component is used to create buttons with various styles and functionalities.
 * 
 * @component
 * @param {Object} props - properties passed to component.
 * @param {string} props.className - additional CSS classes to apply to button.
 * @param {boolean} props.disabled - whether button is disabled.
 * @param {string} props.endIcon - custom or SVG icon to display at end of button.
 * @param {string} props.fillMode - fill mode of button (e.g., 'flat', 'outline', 'solid').
 * @param {string} props.icon - name of icon from KendoReact theme.
 * @param {string} props.iconClass - Additional CSS classes to apply to icon.
 * @param {string} props.imageAlt - Alt text for image.
 * @param {string} props.imageUrl - URL of image to display.
 * @param {string} props.rounded - rounded style of button (e.g., 'small', 'medium', 'large').
 * @param {boolean} props.selected - If true, button is in a selected state.
 * @param {string} props.size - size of button (e.g., 'small', 'medium', 'large').
 * @param {string} props.startIcon - custom or SVG icon to display at start of button.
 * @param {string} props.svgIcon - SVG icon to display on button.
 * @param {string} props.themeColor - theme color of button (e.g., 'primary', 'secondary').
 * @param {string} props.title - Sets the title htmlAttribute of the Button, tooltip will not work without this.
 * @param {boolean} props.togglable - If true, the button can be toggled on and off.
 * @param {string} props.unstyled - If true, button is unstyled.
 * @param {string} props.type - type of button (e.g., 'button', 'submit').
 * @param {function} props.onSubmit - Callback function to call when button is submitted.
 * @param {function} props.onClick - Callback function to call when button is clicked.
 * @returns {JSX.Element}
 * 
 * @see {@link https://www.telerik.com/kendo-react-ui/components/buttons/button/ | Kendo Button Docs}
 * 
 */
export const Button = ({
  className = '',
  disabled = false,
  endIcon,
  fillMode = 'solid',
  icon,
  iconClass = '',
  imageAlt = '',
  imageUrl = '',
  rounded = 'medium',
  selected = false,
  size = 'medium',
  startIcon,
  svgIcon,
  themeColor = 'base',
  title = '',
  content='',
  togglable = false,
  unstyled = '',
  type = 'button',
  onSubmit = () => { },
  onClick = () => { },
}) => {
  // protect against undefined values
  const kendoProps = {
    disabled,
    selected,
    togglable,
    unstyled,
    type,
    ...(title && { title }),
    ...(endIcon && { endIcon }),
    ...(fillMode && { fillMode }),
    ...(icon && { icon }),
    ...(iconClass && { iconClass }),
    ...(imageAlt && { imageAlt }),
    ...(imageUrl && { imageUrl }),
    ...(rounded && { rounded }),
    ...(size && { size }),
    ...(startIcon && { startIcon }),
    ...(themeColor && { themeColor }),
    ...(svgIcon && { svgIcon }),
  };

  return (
    <KendoButton
      className={className}
      {...kendoProps}
      onClick={(e) => {
        onClick(e);
        if (type === 'submit') {
          onSubmit(e);
        }
      }}
    >
      {content}
    </KendoButton>
  );
}